import { setTransformListAccessHandler, setUntrackedRunner } from '@/svgcanvas/mutation-tracking';
import { getDocumentRootAttributes, getExportAttributes } from '@/svgcanvas/svg-exec';
import { cleanupElement } from '@/svgcanvas/utilities';
import { metadataServiceInstance } from './metadata-service';
import { svgEditServiceInstance } from './svg-edit-service';

/**
 * Keeps Blazor's copy of the document up to date with changes made in the editor.
 *
 * Blazor owns the document (CurrentArtworkService); the SVG editor is a projection of it, used for rendering and
 * handling input. Changes made in the editor are observed in the DOM and sent to Blazor as small deltas, in the same
 * form `svgCanvasToString` writes, rather than Blazor re-reading and re-parsing the whole document after every edit.
 *
 * - Elements are referenced by ID. Changes to elements without one (tspans, layer titles) are sent as the serialized
 *   element, as part of its nearest ancestor with an ID.
 * - Editor-only DOM state (dimming while editing inside a group, re-attached metadata while serializing, document
 *   loads, which Blazor takes as a whole new baseline) is excluded using `runUntracked`.
 * - The XMP metadata lives outside the DOM, so it is compared at each flush and sent when it differs.
 *
 * Changes are sent when an undo history entry is made (the end of an operation, so not on every mouse move during a
 * drag), after a short idle period for changes that don't make one, or on request from Blazor (`takeChanges`).
 */

/** A child of a changed parent, in document order: an unchanged element by ID, new or changed content as XML, or text. */
export type ChildEntry = { ref: string } | { xml: string } | { text: string };

/** The complete new attribute set of an element. The document root is identified as {@link ROOT_ID}. */
export interface AttributeChange {
    id: string;
    attributes: [string, string][];
}

/** The complete new child list of an element. The document root is identified as {@link ROOT_ID}. */
export interface ChildrenChange {
    id: string;
    children: ChildEntry[];
}

/** A batch of changes to apply to Blazor's document, in this order: children, then attributes, then metadata. */
export interface DocumentChanges {
    children: ChildrenChange[];
    attributes: AttributeChange[];
    /** The serialized XMP <metadata> element, or null if unchanged. */
    metadata: string | null;
}

export const ROOT_ID = 'svgcontent';

/** How long the DOM must be quiet before changes that made no undo history entry are sent. */
const IDLE_FLUSH_MS = 250;

/** Matches the leading and trailing whitespace `svgToString` trims from text nodes. */
const TRIM_RE = /(?:^\s+|\s+$)/g;

/**
 * Blazor reads attribute values as strings and rejects the whole batch otherwise. The serializer's helpers can produce
 * numbers (shortFloat does), which are harmless when joined into a string but not when sent as JSON.
 */
function asStringPairs(pairs: [string, unknown][]): [string, string][] {
    return pairs.map(([name, value]) => [name, String(value)]);
}

/** The attribute an element's transform list (as given out by `getTransformList`) is written to. */
function transformAttributeName(el: Element): string {
    switch (el.localName) {
        case 'linearGradient':
        case 'radialGradient':
            return 'gradientTransform';
        case 'pattern':
            return 'patternTransform';
        default:
            return 'transform';
    }
}

export class DocumentSyncService {
    private root: SVGSVGElement | null = null;
    private observer: MutationObserver | null = null;
    private dotNetRef: DotNet.DotNetObject | null = null;

    private readonly dirtyAttributes = new Set<Element>();
    private readonly dirtyChildren = new Set<Element>();
    private readonly addedNodes = new Set<Node>();
    private readonly idChanged = new Set<Element>();

    /**
     * Elements whose transform list has been handed out, with their transform attribute as last reported. List edits
     * produce no mutation records (see `noteTransformListAccess`), so these are compared at each collection instead.
     * Elements stay watched until they leave the document, as callers can keep a list and edit it later.
     */
    private readonly watchedTransforms = new Map<Element, string | null>();

    private untrackedDepth = 0;
    private idleFlushTimer: ReturnType<typeof setTimeout> | null = null;
    private lastMetadata: string | null = null;

    public constructor() {
        setUntrackedRunner(fn => this.runUntracked(fn));
        setTransformListAccessHandler(el => this.watchTransform(el));

        // The end of an operation: send its changes now, so Blazor's panels update straight away.
        window.addEventListener('history-changed', () => this.sendChanges());
    }

    /**
     * Sends any pending changes to Blazor now, rather than waiting for the next undo history entry or idle period.
     */
    public sendChanges() {
        this.flush();
    }

    /**
     * Diagnostic, for the browser console: checks Blazor's document matches what the editor would write in full, and
     * logs the first difference if not. Leaves in-group and path editing, as serializing the whole document does.
     */
    public async verify(): Promise<string> {
        if (!this.dotNetRef) {
            return "Not attached to Blazor yet.";
        }

        const result: string = await this.dotNetRef.invokeMethodAsync('VerifyDocumentSync');
        (result === 'In sync' ? console.log : console.warn)(`Document Sync - ${result}`);
        return result;
    }

    /**
     * Starts sending changes to Blazor.
     * @param dotNetRef The object to send changes to. Must have an `ApplyDocumentChanges` method.
     */
    public attach(dotNetRef: DotNet.DotNetObject) {
        this.dotNetRef = dotNetRef;
        this.ensureObserving();
    }

    /**
     * Gets the whole document and makes it the new baseline: nothing changed before this call will be sent. Used after
     * a document is loaded, as the editor normalises what it imports, and as a fallback if a delta can't be applied.
     * @returns The document, as `svgCanvasToString` writes it.
     */
    public getFullDocument(): string {
        this.ensureObserving();

        // Serialize first: it can itself change the document (dropping unused definitions), and the result includes
        // every change so far, so all of them are then covered by the baseline.
        const svg = svgEditServiceInstance.canvas.svgCanvasToString();
        this.discardPending();
        this.lastMetadata = this.serializeMetadata();
        return svg;
    }

    /**
     * Gets the changes made since they were last sent, and marks them as sent. Called by Blazor when it needs its
     * document to be current, for example before resolving a new selection.
     */
    public takeChanges(): DocumentChanges {
        return this.collect();
    }

    /**
     * Runs `fn` without reporting its DOM changes. Changes made before this call are kept.
     */
    public runUntracked<T>(fn: () => T): T {
        if (this.observer) {
            this.recordMutations(this.observer.takeRecords());
        }

        // Transform list edits made before this are changes too, so note them before fn can mask them. (Not when nested,
        // as anything since the outer call started is the outer untracked work.)
        if (this.untrackedDepth === 0) {
            this.checkWatchedTransforms();
        }

        this.untrackedDepth++;
        try {
            return fn();
        } finally {
            this.untrackedDepth--;
            // Discard what fn did. Nested calls discard their own; the outermost discards the rest.
            this.observer?.takeRecords();
            this.refreshWatchedTransforms();
        }
    }

    private ensureObserving() {
        const root = svgEditServiceInstance.canvas?.getSVGContent() as SVGSVGElement | undefined;
        if (!root || root === this.root) {
            return;
        }

        // Loading a document can replace the root element itself, so re-attach to the current one.
        this.observer?.disconnect();
        this.discardPending();
        this.watchedTransforms.clear();
        this.root = root;
        this.observer = new MutationObserver(records => this.recordMutations(records));
        this.observer.observe(root, {
            attributes: true,
            childList: true,
            characterData: true,
            subtree: true,
        });
    }

    private recordMutations(records: MutationRecord[]) {
        if (this.untrackedDepth > 0 || records.length === 0) {
            return;
        }

        for (const record of records) {
            switch (record.type) {
                case 'attributes':
                    this.dirtyAttributes.add(record.target as Element);
                    if (record.attributeName === 'id') {
                        this.idChanged.add(record.target as Element);
                    }
                    break;

                case 'childList':
                    this.dirtyChildren.add(record.target as Element);
                    record.addedNodes.forEach(node => this.addedNodes.add(node));
                    break;

                case 'characterData':
                    if (record.target.parentElement) {
                        this.dirtyChildren.add(record.target.parentElement);
                    }
                    break;
            }
        }

        this.scheduleIdleFlush();
    }

    private watchTransform(el: Element) {
        if (this.untrackedDepth > 0 || this.watchedTransforms.has(el) || !this.root?.contains(el)) {
            return;
        }

        // Reading the attribute makes the browser write out any earlier list edits first, so this is the value before
        // whatever the caller is about to do.
        this.watchedTransforms.set(el, el.getAttribute(transformAttributeName(el)));
        this.scheduleIdleFlush();
    }

    /**
     * Marks watched elements whose transform changed as dirty, and remembers their new values.
     */
    private checkWatchedTransforms() {
        const root = this.root;
        for (const [el, before] of this.watchedTransforms) {
            if (!root || !root.contains(el)) {
                this.watchedTransforms.delete(el);
                continue;
            }

            const current = el.getAttribute(transformAttributeName(el));
            if (current !== before) {
                this.dirtyAttributes.add(el);
                this.watchedTransforms.set(el, current);
            }
        }
    }

    /**
     * Remembers the current transforms of watched elements without reporting changes, after untracked work or when
     * taking a new baseline.
     */
    private refreshWatchedTransforms() {
        for (const el of this.watchedTransforms.keys()) {
            this.watchedTransforms.set(el, el.getAttribute(transformAttributeName(el)));
        }
    }

    private scheduleIdleFlush() {
        if (this.idleFlushTimer !== null) {
            clearTimeout(this.idleFlushTimer);
        }

        this.idleFlushTimer = setTimeout(() => this.flush(), IDLE_FLUSH_MS);
    }

    private flush() {
        if (!this.dotNetRef) {
            return;
        }

        const changes = this.collect();
        if (changes.children.length === 0 && changes.attributes.length === 0 && changes.metadata === null) {
            return;
        }

        const dotNetRef = this.dotNetRef;
        dotNetRef.invokeMethodAsync('ApplyDocumentChanges', changes)
            .catch(err => {
                // The batch never reached ApplyDocumentChanges (or threw in it), so Blazor's own fallback didn't run and
                // its document is now behind. Have it re-read the whole document rather than stay out of sync.
                console.error("Document Sync - Blazor failed to apply document changes. Re-reading the whole document.", err);
                return dotNetRef.invokeMethodAsync('ResyncDocument');
            })
            .catch(err => console.error("Document Sync - Blazor failed to re-read the document.", err));
    }

    private discardPending() {
        this.observer?.takeRecords();
        this.refreshWatchedTransforms();
        this.clearPending();
    }

    private clearPending() {
        this.dirtyAttributes.clear();
        this.dirtyChildren.clear();
        this.addedNodes.clear();
        this.idChanged.clear();

        if (this.idleFlushTimer !== null) {
            clearTimeout(this.idleFlushTimer);
            this.idleFlushTimer = null;
        }
    }

    private collect(): DocumentChanges {
        this.ensureObserving();
        const root = this.root;
        if (!root || !this.observer) {
            return { children: [], attributes: [], metadata: null };
        }

        this.recordMutations(this.observer.takeRecords());
        this.checkWatchedTransforms();

        // Serializing normalises some elements in place (cleanupElement), which is not a document change.
        const changes = this.runUntracked(() => this.buildChanges(root));
        this.clearPending();
        return changes;
    }

    private buildChanges(root: SVGSVGElement): DocumentChanges {
        const isLive = (node: Node) => node === root || root.contains(node);
        const isReferenceable = (el: Element) => el.id !== '' && el.closest('g[data-gsvg]') === null;

        // Elements to send whole, as XML in their parent's child list.
        const replaced = new Set<Element>();
        const childrenTargets = new Set<Element>();
        const attributeTargets = new Set<Element>();
        let rootAttributesChanged = false;

        // Send `el` whole, as part of its nearest ancestor that can be referenced.
        const markReplaced = (el: Element) => {
            let current = el;
            while (current !== root) {
                const parent = current.parentElement;
                if (!parent) {
                    return;
                }

                replaced.add(current);
                if (parent === root || isReferenceable(parent)) {
                    childrenTargets.add(parent);
                    return;
                }

                current = parent;
            }
        };

        const markChildrenChanged = (parent: Element) => {
            if (parent === root || isReferenceable(parent)) {
                childrenTargets.add(parent);
            } else {
                markReplaced(parent);
            }
        };

        for (const node of this.addedNodes) {
            if (!isLive(node) || node === root) {
                continue;
            }

            if (node instanceof Element) {
                markReplaced(node);
            } else if (node.parentElement) {
                markChildrenChanged(node.parentElement);
            }
        }

        for (const parent of this.dirtyChildren) {
            if (isLive(parent)) {
                markChildrenChanged(parent);
            }
        }

        for (const el of this.dirtyAttributes) {
            if (!isLive(el)) {
                continue;
            }

            if (el === root) {
                rootAttributesChanged = true;
            } else if (isReferenceable(el) && !this.idChanged.has(el)) {
                attributeTargets.add(el);
            } else {
                // Blazor knows it by its old ID, or can't reference it at all.
                markReplaced(el);
            }
        }

        // Anything inside an element sent whole is already included in it.
        const isInsideReplaced = (el: Element) => {
            for (let current = el.parentElement; current && current !== root; current = current.parentElement) {
                if (replaced.has(current)) {
                    return true;
                }
            }
            return false;
        };
        const isIncludedElsewhere = (el: Element) => el !== root && (replaced.has(el) || isInsideReplaced(el));

        const children: ChildrenChange[] = [...childrenTargets]
            .filter(parent => !isIncludedElsewhere(parent))
            .map(parent => ({
                id: parent === root ? ROOT_ID : parent.id,
                children: this.serializeChildren(parent, replaced, isReferenceable),
            }));

        const attributes: AttributeChange[] = [...attributeTargets]
            .filter(el => !isIncludedElsewhere(el))
            .map(el => {
                cleanupElement(el);
                return { id: el.id, attributes: asStringPairs(getExportAttributes(el)) };
            });

        if (rootAttributesChanged) {
            cleanupElement(root);
            attributes.push({ id: ROOT_ID, attributes: asStringPairs(getDocumentRootAttributes(root)) });
        }

        const metadata = this.serializeMetadata();
        const metadataChanged = metadata !== this.lastMetadata;
        this.lastMetadata = metadata;

        return { children, attributes, metadata: metadataChanged ? metadata : null };
    }

    private serializeChildren(parent: Element, replaced: Set<Element>, isReferenceable: (el: Element) => boolean): ChildEntry[] {
        const entries: ChildEntry[] = [];

        for (const child of parent.childNodes) {
            switch (child.nodeType) {
                case Node.ELEMENT_NODE: {
                    const el = child as Element;
                    if (!replaced.has(el) && !this.addedNodes.has(el) && isReferenceable(el)) {
                        entries.push({ ref: el.id });
                    } else {
                        const xml = this.serializeElement(el);
                        if (xml !== '') {
                            entries.push({ xml });
                        }
                    }
                    break;
                }

                case Node.TEXT_NODE: {
                    const text = child.nodeValue.replace(TRIM_RE, '');
                    if (text !== '') {
                        entries.push({ text });
                    }
                    break;
                }

                case Node.CDATA_SECTION_NODE:
                    entries.push({ xml: `<![CDATA[${child.nodeValue}]]>` });
                    break;

                case Node.COMMENT_NODE:
                    entries.push({ xml: `<!--${(child as Comment).data}-->` });
                    break;
            }
        }

        return entries;
    }

    /**
     * Serializes an element as `svgCanvasToString` would. Empty <defs> serialize to an empty string, as they are
     * omitted from the document.
     */
    private serializeElement(el: Element): string {
        const canvas = svgEditServiceInstance.canvas;

        // svgCanvasToString unwraps nested SVGs whose wrapper has nothing but an ID or style.
        if (el.tagName === 'g' && el.hasAttribute('data-gsvg') && el.firstElementChild) {
            const significant = [...el.attributes].filter(attr => attr.nodeName !== 'id' && attr.nodeName !== 'style');
            if (significant.length === 0) {
                return canvas.svgToString(el.firstElementChild as SVGElement, 0);
            }
        }

        return canvas.svgToString(el as SVGElement, 0);
    }

    private serializeMetadata(): string {
        return svgEditServiceInstance.canvas.svgToString(metadataServiceInstance.metadataRoot, 0);
    }
}

export const documentSyncServiceInstance = new DocumentSyncService();
window["_zaa_services"] = window["_zaa_services"] || {};
window["_zaa_services"].documentSyncService = documentSyncServiceInstance;
